import {onBlockLoaded} from "@plaudit/frontend-apis";

import accordionIcon from "../../../accordion-toggle.svg?string";

const invocation_marker = 'plaudit-accordion-block--ready';
const open_marker = "accordion--open";
const resizeObserver = new ResizeObserver((entries) => {
	for (const entry of entries) {
		const element = entry.target;
		if (
			element instanceof HTMLElement &&
			element.dataset.plauditAccordionTransitionCount === "0" &&
			element.style.maxHeight &&
			element.style.maxHeight !== "0px" &&
			element.style.maxHeight !== "0"
		) {
			element.style.maxHeight = ""; // We have to unset the maxHeight before recalculating the height, otherwise it can never increase
			element.style.maxHeight = `${entry.target.scrollHeight}px`;
		}
	}
});

function getSVG(): Element {
	const svg =
		(document.getElementById("plaudit-accordion-block-toggle-svg")?.cloneNode(true) as HTMLElement | undefined) ??
		new DOMParser().parseFromString(accordionIcon, "image/svg+xml").documentElement;
	svg.removeAttribute("id");
	return svg;
}

function injectButtonIntoHeading(heading: HTMLElement, id: string) {
	const button = document.createElement("button");

	const svg = getSVG();
	button.setAttribute("aria-expanded", "false");
	button.setAttribute("aria-controls", id);
	// biome-ignore lint/style/noNonNullAssertion: this anchor tag is guaranteed to be present
	const anchor = heading.querySelector("a")!;
	while (anchor.firstChild) {
		button.appendChild(anchor.firstChild);
	}
	button.querySelector(".toggle-icon")?.replaceWith(svg);
	anchor.replaceWith(button);

	return button;
}

function toggleContent(button: HTMLElement, content: HTMLElement, open: boolean): void {
	button.setAttribute("aria-expanded", open.toString());
	if (open) {
		content.style.display = getComputedStyle(content).getPropertyValue("--wp--custom--accordion-content-display") || "block";
		content.style.maxHeight = `${content.scrollHeight}px`;
	} else {
		// While we could set display to none here if Firefox had actually implemented support for "transition-behavior", they haven't as of 07/24/2025
		// See here: https://caniuse.com/mdn-css_properties_display_is_transitionable
		// Instead, we set it within the transitionend listener in attachAccordionTransitionEvents()
		content.style.maxHeight = "0px";
	}
}

function initialize(accordion: HTMLElement) {
	for (const item of Array.from(accordion.children)) {
		if (!(item instanceof HTMLElement) || !item.classList.contains("wp-block-plaudit-accordion-block-item")) {
			continue;
		}
		const heading = item.querySelector<HTMLElement>(":scope > .wp-block-plaudit-accordion-block-heading");
		const content = item.querySelector<HTMLElement>(":scope > .wp-block-plaudit-accordion-block-panel");
		if (!heading || !content) {
			continue;
		}

		let id = heading.id || "accordion-item";
		const priorsWithSameId = document.querySelectorAll(`.wp-block-plaudit-accordion-block-item.plaudit-accordion-item--ready > #${CSS.escape(id)}`).length;
		item.classList.toggle("plaudit-accordion-item--ready", true);
		if (priorsWithSameId > 0) {
			id += `-${priorsWithSameId}`;
		}
		heading.id = id;
		content.id = `${id}-content`;
		resizeObserver.observe(content);
		const button = injectButtonIntoHeading(heading, id);

		const open = (!window.location.hash && item.classList.contains(open_marker)) || (!!window.location.hash && id === window.location.hash.substring(1));
		item.classList.toggle(open_marker, open);

		// We can safely call this because we're explicitly disabling the transitions
		toggleContent(button, content, open);
		if (!open) {
			// We have to set this here because we cannot set it within toggleContent() until FireFox fixes their "support" for "transition-behavior"
			content.style.display = "none";
		}
	}
}

function getButtonAndContent(item: HTMLElement) {
	const button = item.querySelector<HTMLElement>(":scope > .wp-block-plaudit-accordion-block-heading button");
	const content = item.querySelector<HTMLElement>(":scope > .wp-block-plaudit-accordion-block-panel");
	if (!button || !content) {
		throw new TypeError("Button and Content must both exist");
	}
	return {button, content} as const;
}

function updateURLFragment(accordion: HTMLElement, id: string, open: boolean) {
	if (open) {
		// Set the fragment to the id of the accordion we are opening
		const url = new URL(window.location.href);
		url.hash = id;
		history.replaceState(history.state, "", url);
	} else {
		// If the current fragment is the id of the accordion we are closing, remove it from the URL
		if (window.location.hash && window.location.hash.substring(1) === id) {
			const openItem = accordion.querySelector<HTMLElement>(`:scope > .wp-block-plaudit-accordion-block-item.${open_marker}`)
				?? document.querySelector<HTMLElement>(`.wp-block-plaudit-accordion-block-item.${open_marker}`);
			if (!openItem) {
				return;
			}
			const url = new URL(window.location.href);
			url.hash = openItem.querySelector(":scope > .wp-block-plaudit-accordion-block-heading")?.id ?? "";
			history.replaceState(history.state, "", url);
		}
	}
}

function attachListeners(accordion: HTMLElement) {
	if (accordion.dataset.autoclose) {
		accordion.addEventListener("plaudit/accordion-block/accordion-opened", event => {
			const trippedAccordion = (event as CustomEvent<HTMLElement>).detail;
			for (const item of Array.from(accordion.children)) {
				if (!(item instanceof HTMLElement) || !item.classList.contains("wp-block-plaudit-accordion-block-item") || item === trippedAccordion) {
					continue;
				}
				const {button, content} = getButtonAndContent(item);
				item.classList.toggle(open_marker, false);
				toggleContent(button, content, false);
				// We don't update the URL fragment here because it's already guaranteed to be correct
			}
		});
	}

	accordion.addEventListener('click', e => {
		if (!(e.target instanceof Element)) {
			return;
		}
		const heading = e.target.closest(".wp-block-plaudit-accordion-block-heading");
		if (heading?.closest(".wp-block-plaudit-accordion-block-accordion") !== accordion) {
			return;
		}

		const item = heading.parentElement;
		if (item === null) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		const open = item.classList.toggle(open_marker);
		const {button, content} = getButtonAndContent(item);
		item.classList.toggle(open_marker, open);
		toggleContent(button, content, open);
		updateURLFragment(accordion, heading.id, open);

		if (open && accordion.dataset.autoclose) {
			for (const otherItem of Array.from(accordion.children)) {
				if (!(otherItem instanceof HTMLElement) || !otherItem.classList.contains("wp-block-plaudit-accordion-block-item") || otherItem === item) {
					continue;
				}
				const {button, content} = getButtonAndContent(otherItem);
				otherItem.classList.toggle(open_marker, false);
				toggleContent(button, content, false);
				// We don't update the URL fragment here because it's already guaranteed to be correct
			}
		}
	});
	window.addEventListener('hashchange', () => {
		if (window.location.hash.length > 1) { // The hash starts with the "#" character, so we actually only want to treat it as having a value if its length is greater than one
			const targetId = window.location.hash.substring(1);
			for (const item of Array.from(accordion.children)) {
				if (item instanceof HTMLElement && item.classList.contains("wp-block-plaudit-accordion-block-item")) {
					const {button, content} = getButtonAndContent(item);
					if (button.closest(".wp-block-plaudit-accordion-block-heading")?.id === targetId) {
						item.classList.toggle(open_marker, true);
						toggleContent(button, content, true);
					}
				}
			}
		}
	});
}

function attachAnimationHandlers(accordion: HTMLElement) {
	const isAccordionBlockPanel = (target: EventTarget|null): target is HTMLElement => {
		return target instanceof HTMLElement && target.classList.contains("wp-block-plaudit-accordion-block-panel") && target.parentElement?.parentElement === accordion;
	}

	accordion.addEventListener(
		"transitionrun",
		(evt) => {
			if (evt.propertyName === "max-height" && isAccordionBlockPanel(evt.target)) {
				const transitionCount = parseInt(evt.target.dataset.plauditAccordionTransitionCount || "0", 10);
				evt.target.dataset.plauditAccordionTransitionCount = (transitionCount + 1).toString();
			}
		},
		{passive: true},
	);

	const onEnd = (evt: TransitionEvent) => {
		if (evt.propertyName === "max-height" && isAccordionBlockPanel(evt.target)) {
			if (evt.type === "transitionend" && (evt.target.style.maxHeight === "0px" || evt.target.style.maxHeight === "0")) {
				evt.target.style.display = "none";
			}
			const transitionCount = parseInt(evt.target.dataset.plauditAccordionTransitionCount || "0", 10);
			evt.target.dataset.plauditAccordionTransitionCount = (transitionCount - 1).toString();
		}
	};
	accordion.addEventListener("transitionend", onEnd, {passive: true});
	accordion.addEventListener("transitioncancel", onEnd, {passive: true});
}

onBlockLoaded({
	blockOrClassName: "plaudit-accordion-block/accordion",
	onAdd(accordion) {
		if (accordion.classList.contains(invocation_marker)) {
			return;
		}
		initialize(accordion);
		accordion.classList.toggle(invocation_marker, true);
		attachListeners(accordion);
		attachAnimationHandlers(accordion);
		return () => {
			for (const panel of accordion.querySelectorAll(":scope > * > .wp-block-plaudit-accordion-block-panel")) {
				resizeObserver.unobserve(panel);
			}
		}
	}
});
